import React, {
  createContext,
  useContext,
  ReactNode,
} from "react";
import { useRoles as useRolesRegistry } from "../stores/roles.registry";
import { useAuth } from "./AuthContext";

// Define the Role interface
export interface Role {
  id: number;
  code: string;
  name: string;
  description: string;
}

// Define the context type
interface RoleContextType {
  roles: Role[];
  loading: boolean;
  error: string | null;
  refreshRoles: () => Promise<void>;
  getRoleByCode: (code: string) => Promise<Role | undefined>;
  getRoleById: (id: number) => Promise<Role | undefined>;
}

// Create the context with a default undefined value
const RoleContext = createContext<RoleContextType | undefined>(undefined);

// Props for the provider component
interface RoleProviderProps {
  children: ReactNode;
}

// Provider component
export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  // Use centralized RolesRegistry
  const { roles, loading, error, refresh } = useRolesRegistry();
  
  // Get authentication context (for compatibility, but roles are loaded from registry)
  const { currentUser } = useAuth();

  const refreshRoles = async () => {
    await refresh();
  };

  const getRoleByCode = async (code: string): Promise<Role | undefined> => {
    // Si el código es nulo o indefinido, devolver un rol por defecto
    if (!code) {
      console.warn("getRoleByCode recibió un código nulo o indefinido");
      return {
        id: 0,
        code: "unknown",
        name: "Unknown Role",
        description: "Rol desconocido",
      };
    }

    // Import RolesRegistry to use getRoleByCode
    const { RolesRegistry } = await import("../stores/roles.registry");
    const role = await RolesRegistry.getRoleByCode(code);
    
    if (role) return role as Role;

    // Si no tenemos el rol en caché, devolvemos un rol básico para evitar errores
    return {
      id: 0,
      code: code,
      name: code.charAt(0).toUpperCase() + code.slice(1).replace("_", " "),
      description: "",
    };
  };

  const getRoleById = async (id: number): Promise<Role | undefined> => {
    // First check if we already have the role in our state
    const cachedRole = roles.find((role) => role.id === id);
    if (cachedRole) return cachedRole as Role;

    // Import RolesRegistry to use getRoleById
    const { RolesRegistry } = await import("../stores/roles.registry");
    const role = await RolesRegistry.getRoleById(id);
    
    if (role) return role as Role;

    // Si no tenemos el rol en caché, devolvemos un rol básico para evitar errores
    return {
      id: id,
      code: `role_${id}`,
      name: `Role ${id}`,
      description: "",
    };
  };

  const value = {
    roles,
    loading,
    error,
    refreshRoles,
    getRoleByCode,
    getRoleById,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

// Custom hook to use the role context
export const useRoles = (): RoleContextType => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error("useRoles must be used within a RoleProvider");
  }
  return context;
};

export default RoleContext;
